import {
  Button,
  H5,
  H6,
  RadioSelector,
  RadioSelectorOption,
} from "@sellar-io/components";
import { SpinnerLegacy } from "../../../../components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { useOrder } from "../../../../hooks/api/v2/order/useOrder";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { usePaymentList } from "../../../../hooks/api/v2/payment/usePaymentList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { stripeElementsAppearance } from "./stripeElementsAppearance";
import { ReactElement, useState } from "react";
import { usePaymentIntent } from "../../../../hooks/api/v2/paymentIntent/usePaymentIntent";
import { ORDER_SUCCESS_BUYER } from "../../../../constants/routes";
import { Alert } from "../../../../components/alert/Alert";
import { capitalize, formatCurrency } from "@sellar-io/common";
import { usePaymentMethodList } from "../../../../hooks/api/v2/paymentMethod/usePaymentMethodList";
import { twMerge } from "tailwind-merge";

type PaymentFailedDialogProps = {
  orderId: number;
  active: boolean;
  onDismiss: () => void;
};

export const PaymentFailedDialog = (props: PaymentFailedDialogProps) => {
  return (
    <Dialog active={props.active} onDismiss={props.onDismiss}>
      <PaymentFailedDialogContentWrapper {...props} />
    </Dialog>
  );
};

const PaymentFailedDialogContentWrapper = ({
  orderId,
  onDismiss,
}: PaymentFailedDialogProps) => {
  if (!orderId) throw new Error("No order Id found");
  const orderQuery = useOrder(orderId);
  const order = orderQuery.data;
  return (
    <>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        title={"Choose payment card"}
      />
      {orderQuery.isPending ? (
        <SpinnerLegacy />
      ) : (
        <>
          {order && (
            <PaymentFailedDialogContentContainer
              order={order}
              onDismiss={onDismiss}
            />
          )}
        </>
      )}
    </>
  );
};

const PaymentFailedDialogContentContainer = ({
  onDismiss,
  order,
}: {
  order: OrderGetResponseType;
  onDismiss: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  if (!order) throw new Error("No order found");

  const paymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: order.id,
        },
      ],
      limit: 1,
    },
    {
      enabled: order.paymentType === "checkout" && !!business?.Retailer,
    },
  );

  const stripePromise = loadStripe(
    import.meta.env.VITE_STRIPE_PUBLIC_KEY || "",
    {
      stripeAccount: order.Supplier.stripeAccountId ?? undefined,
    },
  );

  const loading = paymentsQuery.isPending;
  const payment = paymentsQuery.data?.pages[0]?.[0];

  if (loading) {
    return (
      <DialogContent>
        <SpinnerLegacy />
      </DialogContent>
    );
  }

  if (!payment) {
    return <DialogContent>No existing payment found</DialogContent>;
  }

  return (
    <DialogContent>
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          currency: "gbp",
          amount: order.price,
          setup_future_usage: "on_session",
          allowedPaymentMethodTypes: ["card"],
          appearance: stripeElementsAppearance,
        }}
      >
        <PaymentFailedDialogContent
          onDismiss={onDismiss}
          supplierId={order.SupplierId}
          orderId={order.id}
          price={order.price}
          stripePaymentIntentId={payment.paymentIntentId}
        />
      </Elements>
    </DialogContent>
  );
};

const PaymentFailedDialogContent = ({
  onDismiss,
  stripePaymentIntentId,
  price,
  orderId,
  supplierId,
}: {
  stripePaymentIntentId: string;
  price: number;
  orderId: number;
  supplierId: number;
  onDismiss: () => void;
}) => {
  const [updatedStripePaymentMethodId, setUpdatedStripePaymentMethodId] =
    useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();

  const paymentIntentQuery = usePaymentIntent(stripePaymentIntentId);
  const isPaymentIntentQueryLoading = paymentIntentQuery.isPending;

  const onSubmit = async () => {
    setLoading(true);
    setErrorMessage(undefined);
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setErrorMessage(
        "Stripe components are still loading. Please try again shortly.",
      );
      setLoading(false);
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setLoading(false);
      return;
    }

    const clientSecret = paymentIntentQuery.data?.clientSecret;
    if (!clientSecret) {
      return setErrorMessage("An error has occurred. Please contact support.");
    }

    return confirmStripePayment({ clientSecret });
  };

  const confirmStripePayment = async ({
    clientSecret,
  }: {
    clientSecret: string;
  }) => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      setErrorMessage(
        "Stripe components are still loading. Please try again shortly.",
      );
      setLoading(false);
      return;
    }
    const orderSuccessUrl = ORDER_SUCCESS_BUYER.href({
      supplierIdOrUrlId: supplierId,
      orderId,
    });
    try {
      await stripe
        .confirmPayment({
          ...((!updatedStripePaymentMethodId ||
            updatedStripePaymentMethodId === "new-card") && {
            elements,
          }),
          clientSecret,
          confirmParams: {
            return_url: `${import.meta.env.VITE_URL}${orderSuccessUrl}`,
            ...(updatedStripePaymentMethodId && {
              payment_method: updatedStripePaymentMethodId,
            }),
          },
        })
        .then((res) => {
          if (res.error) {
            setLoading(false);
            return setErrorMessage(res.error.message);
          }

          onDismiss();
        });
    } catch (e) {
      console.log(e);
      setErrorMessage("Error creating payment");
      setLoading(false);
    }
  };
  return (
    <>
      {errorMessage && (
        <Alert
          message={errorMessage}
          type={"error"}
          className={"mb-2 w-full"}
        />
      )}
      <PaymentStripeCardSelection
        supplierId={supplierId}
        value={updatedStripePaymentMethodId}
        onChange={(e) => setUpdatedStripePaymentMethodId(e)}
      />
      <DialogCTA>
        <Button
          disabled={isPaymentIntentQueryLoading || loading}
          loading={isPaymentIntentQueryLoading || loading}
          className={"w-full"}
          onClick={async () => await onSubmit()}
        >
          Pay {formatCurrency(price)}
        </Button>
      </DialogCTA>
    </>
  );
};

const PaymentStripeCardSelection = ({
  supplierId,
  onChange,
  value,
}: {
  supplierId: number;
  value: string | undefined;
  onChange: (paymentMethodId: string | undefined) => void;
}): ReactElement => {
  const query = usePaymentMethodList({
    query: [
      {
        value: supplierId,
        type: "SupplierId",
        modifier: "EQUALS",
      },
    ],
  });

  if (query.isPending) return <SpinnerLegacy />;
  const cards = query.data?.pages[0];
  if (!value && value !== "") {
    onChange(cards?.[0]?.Suppliers[0]?.stripePaymentMethodId ?? undefined);
  }

  return (
    <>
      {cards?.length ? (
        <>
          <RadioSelector
            onChange={(e) => onChange(e.currentTarget.value)}
            value={value ?? ""}
          >
            {cards.map((card) => (
              <RadioSelectorOption
                key={card.Suppliers[0]?.stripePaymentMethodId}
                value={card.Suppliers[0]?.stripePaymentMethodId ?? ""}
              >
                <div className="flex flex-row justify-between">
                  <div className="flex flex-row gap-2">
                    <H5>{capitalize(card.brand ?? "")} Debit</H5>
                    <H6>Ending in {card.last4}</H6>
                  </div>
                </div>
              </RadioSelectorOption>
            ))}
            <RadioSelectorOption value={"new-card"}>
              <H5>Add new payment card</H5>
              {value === "new-card" && (
                <PaymentStripeCreateCard className={"mt-4"} />
              )}
            </RadioSelectorOption>
          </RadioSelector>
        </>
      ) : (
        <RadioSelector
          onChange={(e) => onChange(e.currentTarget.value)}
          value={value ?? ""}
        >
          <RadioSelectorOption value={"new-card"}>
            <H5>Add new payment card</H5>
            <PaymentStripeCreateCard className={"mt-4"} />
          </RadioSelectorOption>
        </RadioSelector>
      )}
    </>
  );
};

const PaymentStripeCreateCard = ({
  className,
}: {
  className?: string;
}): ReactElement => {
  return (
    <div className={twMerge(className)}>
      <PaymentElement options={{ paymentMethodOrder: ["card"] }} />
    </div>
  );
};
